import { useCallback, useEffect, useState } from 'react';
import {
  Newspaper,
  Plus,
  Pencil,
  Trash2,
  RefreshCw,
  ChevronUp,
  ChevronDown,
  ImageOff,
  ExternalLink,
} from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { Badge } from '../components/ui/Badge';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../components/ui/Table';
import { useToast } from '../components/ui/Toast';
import { api } from '../services/api';
import { formatDate } from '../utils/helpers';
import type { BlogPressArticle, BlogPressArticlePayload, BlogPressCategory } from '../types';

// ── Category options (match backend/app/services/blog_press_config.py and the
// public website's category filter pills) ──────────────────────────────────

const CATEGORY_OPTIONS: { id: BlogPressCategory; label: string }[] = [
  { id: 'blog', label: 'Blog' },
  { id: 'article', label: 'Article' },
  { id: 'newsletter', label: 'Newsletter' },
  { id: 'press', label: 'Press' },
];

const CATEGORY_FILTERS: { id: 'all' | BlogPressCategory; label: string }[] = [
  { id: 'all', label: 'All' },
  ...CATEGORY_OPTIONS,
];

const CATEGORY_BADGE_VARIANT: Record<BlogPressCategory, 'info' | 'success' | 'warning' | 'default'> = {
  blog: 'info',
  article: 'success',
  newsletter: 'warning',
  press: 'default',
};

function categoryLabel(category: BlogPressCategory): string {
  return CATEGORY_OPTIONS.find((option) => option.id === category)?.label ?? category;
}

// ── Form state ───────────────────────────────────────────────────────────────

type ArticleForm = {
  category: BlogPressCategory;
  title: string;
  slug: string;
  description: string;
  image_url: string;
  author: string;
  published_at: string; // yyyy-mm-dd, for <input type="date">
  content: string;
  read_more_url: string;
  is_published: boolean;
};

const emptyForm: ArticleForm = {
  category: 'blog',
  title: '',
  slug: '',
  description: '',
  image_url: '',
  author: 'OBAET',
  published_at: '',
  content: '',
  read_more_url: '',
  is_published: false,
};

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function toDateInputValue(value: string | null): string {
  if (!value) return '';
  return value.slice(0, 10);
}

const inputClass =
  'w-full px-3.5 py-2.5 bg-[#1c1c2a] border border-[#252535] rounded-xl text-sm text-[#f1f5f9] placeholder-[#64748b] focus:outline-none focus:border-[#3b82f6] transition-colors';

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-1.5">
      {children}
    </label>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function BlogPress() {
  const { toast } = useToast();

  const [articles, setArticles] = useState<BlogPressArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<'all' | BlogPressCategory>('all');

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [slugTouched, setSlugTouched] = useState(false);
  const [form, setForm] = useState<ArticleForm>(emptyForm);
  const [saving, setSaving] = useState(false);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [movingId, setMovingId] = useState<string | null>(null);

  const fetchArticles = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      try {
        setError(null);
        const data = await api.getBlogPressArticles();
        setArticles(data);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load Blog/Press articles';
        setError(message);
        if (isRefresh) toast(message, 'error');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [toast]
  );

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  // Silent refetch after a mutation — keeps the table in sync with the
  // server (display_order renormalization, slug normalization, etc.)
  // without flashing the full-page loading spinner.
  const refetchSilently = useCallback(async () => {
    try {
      const data = await api.getBlogPressArticles();
      setArticles(data);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to refresh articles', 'error');
    }
  }, [toast]);

  const openCreate = () => {
    setEditingId(null);
    setSlugTouched(false);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (article: BlogPressArticle) => {
    setEditingId(article.id);
    setSlugTouched(true); // never auto-overwrite an existing article's slug
    setForm({
      category: article.category,
      title: article.title,
      slug: article.slug,
      description: article.description,
      image_url: article.image_url,
      author: article.author,
      published_at: toDateInputValue(article.published_at),
      content: article.content,
      read_more_url: article.read_more_url,
      is_published: article.is_published,
    });
    setModalOpen(true);
  };

  const handleTitleChange = (value: string) => {
    setForm((f) => ({
      ...f,
      title: value,
      // Auto-fill the slug from the title until the admin edits it directly.
      slug: !editingId && !slugTouched ? slugify(value) : f.slug,
    }));
  };

  const handleSlugChange = (value: string) => {
    setSlugTouched(true);
    setForm((f) => ({ ...f, slug: value }));
  };

  const saveArticle = async () => {
    const title = form.title.trim();
    if (!title) {
      toast('Title is required.', 'error');
      return;
    }

    const imageUrl = form.image_url.trim();
    if (imageUrl && !/^https?:\/\//i.test(imageUrl)) {
      toast('Cover Image URL must start with http:// or https:// (or be left blank).', 'error');
      return;
    }

    const readMoreUrl = form.read_more_url.trim();
    if (readMoreUrl && !/^https?:\/\//i.test(readMoreUrl)) {
      toast('Read More URL must start with http:// or https:// (or be left blank).', 'error');
      return;
    }

    const payload: BlogPressArticlePayload = {
      slug: form.slug.trim(),
      category: form.category,
      title,
      description: form.description.trim(),
      content: form.content.trim(),
      image_url: imageUrl,
      author: form.author.trim() || 'OBAET',
      published_at: form.published_at || null,
      read_more_url: readMoreUrl,
      is_published: form.is_published,
    };

    try {
      setSaving(true);
      if (editingId) {
        await api.updateBlogPressArticle(editingId, payload);
        toast('Article updated.', 'success');
      } else {
        await api.createBlogPressArticle(payload);
        toast('Article created.', 'success');
      }
      setModalOpen(false);
      await refetchSilently();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to save article.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteArticle = async (article: BlogPressArticle) => {
    if (!confirm(`Delete "${article.title || 'this article'}"? This cannot be undone.`)) return;

    setDeletingId(article.id);
    try {
      await api.deleteBlogPressArticle(article.id);
      toast('Article deleted.', 'success');
      await refetchSilently();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to delete article.', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  // Reordering only makes unambiguous sense across the full, unfiltered
  // list — moving "up" within a filtered category view would silently
  // reorder relative to articles the admin can't currently see.
  const canReorder = categoryFilter === 'all';

  const moveArticle = async (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= articles.length) return;

    const reordered = [...articles];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(targetIndex, 0, moved);
    setArticles(reordered); // optimistic

    setMovingId(moved.id);
    try {
      const saved = await api.reorderBlogPressArticles(reordered.map((a) => a.id));
      setArticles(saved);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to reorder articles.', 'error');
      await refetchSilently(); // revert to server truth
    } finally {
      setMovingId(null);
    }
  };

  const visibleArticles = articles.filter(
    (article) => categoryFilter === 'all' || article.category === categoryFilter
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin w-8 h-8 border-2 border-[#3b82f6] border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  if (error && articles.length === 0) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-12 h-12 bg-red-500/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <span className="text-[#ef4444] text-xl">!</span>
            </div>
            <p className="text-[#ef4444] font-medium">Failed to load Blog/Press articles</p>
            <p className="text-[#64748b] text-sm mt-1">{error}</p>
            <button
              onClick={() => fetchArticles()}
              className="mt-4 px-4 py-2 text-sm rounded-xl bg-[#141420] border border-[#252535] text-[#94a3b8] hover:text-[#f1f5f9] hover:border-[#353548] transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-[#f8fafc] flex items-center gap-2">
              <Newspaper className="w-6 h-6 text-[#3b82f6]" />
              Blog/Press
            </h1>
            <p className="mt-1 text-sm text-[#94a3b8]">
              Manage articles shown on obaet.com/blog-press — drafts stay hidden from the public site.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              icon={<RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />}
              onClick={() => fetchArticles(true)}
              disabled={refreshing}
            >
              Refresh
            </Button>
            <Button icon={<Plus className="w-4 h-4" />} onClick={openCreate}>
              Add Article
            </Button>
          </div>
        </div>

        <Card padding="none">
          <div className="p-6 pb-0">
            <CardHeader
              title="Articles"
              subtitle={
                canReorder
                  ? 'Use the arrows to reorder — this is the order shown on the public site.'
                  : 'Switch to "All" to reorder articles.'
              }
            />
            <div className="flex flex-wrap gap-2 mb-5">
              {CATEGORY_FILTERS.map((filter) => {
                const isActive = filter.id === categoryFilter;
                return (
                  <button
                    key={filter.id}
                    type="button"
                    onClick={() => setCategoryFilter(filter.id)}
                    aria-pressed={isActive}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      isActive
                        ? 'bg-[#3b82f6]/15 border-[#3b82f6] text-[#3b82f6]'
                        : 'border-[#252535] bg-[#1c1c2a] text-[#94a3b8] hover:text-[#f1f5f9] hover:border-[#353548]'
                    }`}
                  >
                    {filter.label}
                  </button>
                );
              })}
            </div>
          </div>

          {visibleArticles.length === 0 ? (
            <p className="text-sm text-[#64748b] py-12 text-center">
              {articles.length === 0 ? 'No articles yet. Click "Add Article" to create one.' : 'No articles in this category.'}
            </p>
          ) : (
            <div className="px-6 pb-6">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader className="w-20">Cover</TableHeader>
                    <TableHeader>Title</TableHeader>
                    <TableHeader>Category</TableHeader>
                    <TableHeader>Author</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Date</TableHeader>
                    {canReorder && <TableHeader className="w-20">Order</TableHeader>}
                    <TableHeader className="text-right">Actions</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleArticles.map((article) => {
                    const trueIndex = articles.findIndex((a) => a.id === article.id);
                    return (
                      <TableRow key={article.id}>
                        <TableCell>
                          <div className="w-12 h-12 rounded-lg overflow-hidden border border-[#252535] bg-[#0f0f1a] flex items-center justify-center shrink-0">
                            {article.image_url ? (
                              <img
                                src={article.image_url}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <ImageOff className="w-4 h-4 text-[#64748b]" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5 max-w-xs">
                            <p className="font-medium text-[#f1f5f9] truncate">{article.title}</p>
                            {article.read_more_url && (
                              <span title={`Read More links externally: ${article.read_more_url}`}>
                                <ExternalLink className="w-3.5 h-3.5 text-[#64748b] shrink-0" />
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-[#64748b] truncate">/blog-press/{article.slug}</p>
                        </TableCell>
                        <TableCell>
                          <Badge variant={CATEGORY_BADGE_VARIANT[article.category]}>
                            {categoryLabel(article.category)}
                          </Badge>
                        </TableCell>
                        <TableCell>{article.author || '-'}</TableCell>
                        <TableCell>
                          {article.is_published ? (
                            <Badge variant="success">Published</Badge>
                          ) : (
                            <Badge variant="warning">Draft</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-[#94a3b8]">
                          {formatDate(article.published_at || article.created_at || undefined)}
                        </TableCell>
                        {canReorder && (
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                aria-label="Move up"
                                disabled={trueIndex <= 0 || movingId !== null}
                                onClick={() => moveArticle(trueIndex, -1)}
                                className="w-7 h-7 rounded-lg border border-[#252535] bg-[#1c1c2a] text-[#94a3b8] flex items-center justify-center hover:text-[#f1f5f9] hover:border-[#353548] transition-colors disabled:opacity-30 disabled:hover:text-[#94a3b8] disabled:hover:border-[#252535]"
                              >
                                <ChevronUp className="w-3.5 h-3.5" />
                              </button>
                              <button
                                type="button"
                                aria-label="Move down"
                                disabled={trueIndex >= articles.length - 1 || movingId !== null}
                                onClick={() => moveArticle(trueIndex, 1)}
                                className="w-7 h-7 rounded-lg border border-[#252535] bg-[#1c1c2a] text-[#94a3b8] flex items-center justify-center hover:text-[#f1f5f9] hover:border-[#353548] transition-colors disabled:opacity-30 disabled:hover:text-[#94a3b8] disabled:hover:border-[#252535]"
                              >
                                <ChevronDown className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </TableCell>
                        )}
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="secondary"
                              size="sm"
                              icon={<Pencil className="w-3.5 h-3.5" />}
                              onClick={() => openEdit(article)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              icon={<Trash2 className="w-3.5 h-3.5" />}
                              loading={deletingId === article.id}
                              onClick={() => deleteArticle(article)}
                            >
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId ? 'Edit Article' : 'Add Article'}
        size="lg"
        allowDismiss={!saving}
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={saveArticle} loading={saving}>
              {editingId ? 'Save Changes' : 'Create Article'}
            </Button>
          </>
        }
      >
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <FieldLabel>Category</FieldLabel>
              <select
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as BlogPressCategory }))}
                className={inputClass}
              >
                {CATEGORY_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <Input
              label="Author"
              value={form.author}
              onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))}
              placeholder="OBAET"
            />
          </div>

          <Input
            label="Title"
            value={form.title}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder="Article title"
          />

          <div>
            <Input
              label="Slug"
              value={form.slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              placeholder="auto-generated-from-title"
            />
            <p className="text-xs text-[#64748b] mt-1.5">
              URL on the public site: obaet.com/blog-press/{form.slug || 'your-slug'}. Leave blank to
              auto-generate from the title — must be unique.
            </p>
          </div>

          <div>
            <FieldLabel>Short Description</FieldLabel>
            <textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              className={inputClass}
              placeholder="Shown on article cards in the listing."
            />
          </div>

          <Input
            label="Cover Image URL"
            value={form.image_url}
            onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))}
            placeholder="https://... (leave blank to show the placeholder)"
          />

          <Input
            label="Published Date"
            type="date"
            value={form.published_at}
            onChange={(e) => setForm((f) => ({ ...f, published_at: e.target.value }))}
          />

          <div>
            <FieldLabel>Full Article Content</FieldLabel>
            <textarea
              rows={12}
              value={form.content}
              onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
              className={`${inputClass} resize-y min-h-[180px]`}
              placeholder="The complete article body, shown on OBAET's internal detail page."
            />
            <p className="text-xs text-[#64748b] mt-1.5">
              Opens on obaet.com/blog-press/{form.slug || '[slug]'} — unless a Read More URL is set below.
            </p>
          </div>

          <div>
            <Input
              label="Read More URL (optional)"
              value={form.read_more_url}
              onChange={(e) => setForm((f) => ({ ...f, read_more_url: e.target.value }))}
              placeholder="https://... external article or publication"
            />
            <p className="text-xs text-[#64748b] mt-1.5">
              Leave blank to send readers to the internal detail page above. Set this only when "Read
              more" should open an external article or publication instead.
            </p>
          </div>

          <label className="flex items-center gap-3 rounded-xl border border-[#252535] bg-[#0f0f1a] px-4 py-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_published}
              onChange={(e) => setForm((f) => ({ ...f, is_published: e.target.checked }))}
              className="rounded border-[#252535]"
            />
            <span className="text-sm font-medium text-[#f1f5f9]">
              {form.is_published ? 'Published' : 'Draft'}
            </span>
            <span className="text-xs text-[#64748b]">
              — visible on the public site only while Published
            </span>
          </label>
        </div>
      </Modal>
    </Layout>
  );
}
